import { z } from "zod";
import { teamMemberRoleZod } from "./enums.zod.js";
import { zPhoneNumber, zString, zTimeEpoch, zTimestamps } from "./zod_utils.js";
/**
 * TeamApp Schemas
 */
export const teamAppAutoGeneratedZod = z.object({
    id: z.ulid(),
});
export const teamAppMandatoryZod = z.object({
    name: zString.min(1, "Team name is required"),
});
export const teamAppOptionalZod = z.object({
    logoUrl: z.url().max(2048).nullable(),
    createdByUserId: z.uuid(),
    personalTeamForUserId: z.uuid().nullable(),
    deletedAt: zTimeEpoch.nullable(),
});
export const teamAppCreateInputZod = teamAppMandatoryZod.extend(teamAppOptionalZod.partial().shape);
// API-facing create schema. Excludes server-controlled identity/tenancy fields
// (`createdByUserId`, `personalTeamForUserId`, `deletedAt`) so authenticated
// clients can't spoof team ownership or burn another user's personal-team slot
// (soft-delete-partial unique on `personal_team_for_user_id`). The server
// derives `createdByUserId` from the session and never accepts
// `personalTeamForUserId` from clients — it's set only by server-only paths
// (`getDefaultTeam` / `UserTable.afterCreate`).
export const teamAppCreateApiInputZod = teamAppMandatoryZod.extend(teamAppOptionalZod.pick({ logoUrl: true }).partial().shape);
export const teamAppSelectAllZod = teamAppMandatoryZod
    .extend(teamAppOptionalZod.shape)
    .extend(teamAppAutoGeneratedZod.shape)
    .extend(zTimestamps);
/**
 * TeamMember Schemas
 */
export const teamAppMemberAutoGeneratedZod = z.object({
    id: z.ulid(),
});
export const teamAppMemberMandatoryZod = z.object({
    teamId: z.ulid(),
    role: teamMemberRoleZod,
    addedAt: zTimeEpoch,
});
export const teamAppMemberOptionalZod = z.object({
    userId: z.uuid().nullable(),
    email: z.email().max(320).nullable(),
    phoneNumber: zPhoneNumber.nullable(),
    joinedAt: zTimeEpoch.nullable(),
    deletedAt: zTimeEpoch.nullable(),
});
export const teamAppMemberSelectAllZod = teamAppMemberMandatoryZod
    .extend(teamAppMemberOptionalZod.shape)
    .extend(teamAppMemberAutoGeneratedZod.shape)
    .extend(zTimestamps);
export const teamAppMemberRoleZod = teamMemberRoleZod;
// `teamId` is derived from the `x-team-id` header on the RPC procedure, not
// from the request body — callers implicitly add members to their active team.
export const teamAppMemberAddInputZod = z
    .object({
    email: z.email().max(320).optional().nullable(),
    phoneNumber: zPhoneNumber.optional().nullable(),
    role: teamMemberRoleZod,
})
    .refine((data) => data.email || data.phoneNumber, {
    message: "Either email or phone number must be provided",
    path: ["email"],
});
export const teamWithRoleZod = teamAppSelectAllZod.extend({
    joinedAt: zTimeEpoch.nullable(),
    userRole: teamMemberRoleZod,
});
/**
 * Super-admin membership management.
 *
 * Unlike `teamAppMemberAddInputZod` the team is named in the body: a
 * super-admin acts on any workspace, and there is no active-team header that
 * could stand for "the workspace I am administering".
 *
 * Email only. A phone member is created by the phone-OTP flow, which has no
 * SMS provider behind it yet, so offering it here would add a member nobody can
 * ever sign in as.
 */
export const adminTeamMembersInputZod = z.object({
    teamId: z.ulid(),
});
export const adminMemberRowZod = teamAppMemberSelectAllZod.extend({
    /** Whether this member's user has an email + password login (as opposed to Google only). */
    hasPasswordLogin: z.boolean(),
});
/**
 * The password bounds better-auth enforces (its defaults: 8 to 128). Held here
 * so the dialog can tell the operator before the round trip does.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const adminPasswordZod = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
export const adminAddMemberInputZod = z.object({
    teamId: z.ulid(),
    /** Stored lower-cased: better-auth lower-cases the address at sign-up, and an invite is matched to the new user by exact string. */
    email: z
        .email()
        .max(320)
        .transform((value) => value.toLowerCase()),
    role: teamMemberRoleZod,
    /**
     * Create an email + password login for this address when it has no account
     * yet. Ignored when the address already belongs to a user — that person keeps
     * whatever login they have, and a forgotten password is a reset, not an add.
     */
    createPasswordLogin: z.boolean().default(false),
    /** The initial password. Omitted → one is generated and returned once. */
    password: adminPasswordZod.optional(),
    /** Display name for a newly created account. Omitted → the part of the address before the `@`. */
    name: zString.min(1).max(200).optional(),
});
export const adminAddMemberOutputZod = z.object({
    member: teamAppMemberSelectAllZod,
    /** True when this call created a user account (and its personal workspace). */
    accountCreated: z.boolean(),
    /** Set only when a password was generated here. Shown once; it is not stored in a recoverable form. */
    generatedPassword: z.string().nullable(),
});
export const adminResetPasswordInputZod = z.object({
    email: z
        .email()
        .max(320)
        .transform((value) => value.toLowerCase()),
    /** Omitted → one is generated and returned once. */
    password: adminPasswordZod.optional(),
});
export const adminResetPasswordOutputZod = z.object({
    email: z.string(),
    generatedPassword: z.string().nullable(),
    /** How many signed-in sessions the reset ended. */
    sessionsEnded: z.int().min(0),
});
//# sourceMappingURL=team_app.zod.js.map